import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Pagination from "@/components/ventrix/Pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/components/ventrix/atoms";

const ROLES = ["customer", "ops", "finance", "admin", "super_admin"];
// Only a super admin may grant the admin/super_admin roles or edit those
// accounts — mirrors the server-side rule so the UI never offers a rejected action.
const NON_SUPER_ROLES = ["customer", "ops", "finance"];
const LIMIT = 50;

export function Users() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const assignable = isSuper ? ROLES : NON_SUPER_ROLES;
  const locked = (u) => !isSuper && (u.role === "admin" || u.role === "super_admin");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const load = () => {
    setItems(null);
    api.get(`/v1/admin/users?limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [offset]); // eslint-disable-line

  const changeRole = async (u, role) => {
    try { await api.patch(`/v1/admin/users/${u.id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/v1/admin/users/${u.id}`, { disabled: !u.disabled }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <PortalLayout section="admin">
      <div className="vtx-eyebrow">/ Admin · Users</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Users & roles</h1>
      <div className="vtx-card mt-6 overflow-x-auto">
        {items === null && <div className="p-6 space-y-2">{[1,2,3].map(i=><div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && (
          <table className="vtx-table w-full">
            <thead><tr><th>Email</th><th>Name</th><th>Company</th><th>Role</th><th>MFA</th><th>Account</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {items.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="font-mono text-xs">{u.email}</td>
                  <td>{u.name}</td>
                  <td className="text-[#475569]">{u.company || "—"}</td>
                  <td>
                    {locked(u) ? (
                      <span className="text-xs font-semibold text-[#475569] capitalize" data-testid={`role-locked-${u.email}`}>{u.role.replace("_"," ")}</span>
                    ) : (
                      <Select value={u.role} onValueChange={(v)=>changeRole(u, v)}>
                        <SelectTrigger className="max-w-[160px]" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{assignable.map(r => <SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </td>
                  <td>{u.mfa_enabled ? <span className="text-[#166534] font-bold text-xs">ON</span> : <span className="text-[#475569] text-xs">off</span>}</td>
                  <td className="font-mono text-xs">{u.account_id || "—"}</td>
                  <td>{u.disabled ? <span className="text-[#d41f26] font-bold text-xs">DISABLED</span> : <span className="text-[#166534] font-bold text-xs">ACTIVE</span>}</td>
                  <td>{locked(u) ? <span className="text-xs text-[#475569]">—</span> : <button className="text-xs text-[#d41f26] font-bold hover:underline" onClick={()=>toggle(u)} data-testid={`toggle-${u.email}`}>{u.disabled ? "Enable" : "Disable"}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>
    </PortalLayout>
  );
}

export function AuditLog() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    setItems(null);
    api.get(`/v1/admin/audit?limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  }, [offset]);
  return (
    <PortalLayout section="admin">
      <div className="vtx-eyebrow">/ Admin · Audit</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Immutable audit log</h1>
      <p className="text-sm text-[#475569] mt-1">Every mutation is recorded with actor, IP, and user-agent.</p>
      <div className="vtx-card mt-6 overflow-x-auto">
        {items === null && <div className="p-6 space-y-2">{[1,2,3,4,5].map(i=><div key={i} className="vtx-skeleton h-10" />)}</div>}
        {items && (
          <table className="vtx-table w-full">
            <thead><tr><th>Timestamp</th><th>Actor</th><th>Action</th><th>Target</th><th>IP</th></tr></thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id}>
                  <td className="font-mono text-xs whitespace-nowrap">{formatDate(a.timestamp)}</td>
                  <td className="font-mono text-xs">{a.actor_id || "system"}</td>
                  <td className="font-semibold text-[#0E2F76]">{a.action}</td>
                  <td className="text-xs">{a.target_type}:{a.target_id || "—"}</td>
                  <td className="font-mono text-xs text-[#475569]">{a.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>
    </PortalLayout>
  );
}
